// What a deal's cost screens read, worked out from its tenant bindings.
//
// The binding IS the scope: it says this tenant, on this deployment, belongs to
// this deal, from this date. So the chip row and the tenant row are read off the
// bindings in force for the month in view — a tenant that moved to another deal
// in July is still Kingsport's in June, and a screen that read the facts instead
// would put it in both.

import type { TenantBinding } from "@crm/shared";
import { isNonprod } from "./deploymentSelection.js";

export interface UsageScopePart {
  readonly deploymentSlug: string;
  readonly tenantSlug: string;
}

/**
 * The bindings covering a month.
 *
 * Compared at month grain on purpose: a binding that opens on the 12th owns the
 * month it opened in, which is the same rule the evaluator resolves a binding
 * by (as of the last day of the period).
 */
export function bindingsInForce(
  bindings: readonly TenantBinding[],
  period: string,
): TenantBinding[] {
  return bindings.filter((binding) => {
    const from = binding.effective_from.slice(0, 7);
    const to = binding.effective_to === null ? null : binding.effective_to.slice(0, 7);
    return from <= period && (to === null || to >= period);
  });
}

/** Production first, then nonprod, each alphabetical: the order the chips are drawn in. */
export function deploymentsOf(bindings: readonly TenantBinding[]): string[] {
  const slugs = [...new Set(bindings.map((binding) => binding.deployment_slug))];
  return slugs.sort((left, right) => {
    const environment = Number(isNonprod(left)) - Number(isNonprod(right));
    return environment !== 0 ? environment : left.localeCompare(right);
  });
}

export function tenantsOf(bindings: readonly TenantBinding[]): string[] {
  return [...new Set(bindings.map((binding) => binding.tenant_slug))].sort((left, right) =>
    left.localeCompare(right),
  );
}

/**
 * One part per binding the chips and the tenant row left on.
 *
 * A part is a (deployment, tenant) PAIR that a binding says exists — never the
 * cross product of the two selections, which would ask the API for months of a
 * tenant on a deployment it was never on.
 */
export function scopeParts(
  bindings: readonly TenantBinding[],
  deployments: readonly string[],
  tenants: readonly string[],
): UsageScopePart[] {
  const parts = new Map<string, UsageScopePart>();
  for (const binding of bindings) {
    if (!deployments.includes(binding.deployment_slug)) continue;
    if (!tenants.includes(binding.tenant_slug)) continue;
    parts.set(`${binding.deployment_slug} ${binding.tenant_slug}`, {
      deploymentSlug: binding.deployment_slug,
      tenantSlug: binding.tenant_slug,
    });
  }
  return [...parts.values()];
}
